import { BusinessObjectBase } from './base';
import type { Candidate } from './candidate';

export interface PersonalInformationData {
  readonly id: number;
  readonly lastName?: string;
  readonly firstName?: string;
  readonly patronymic?: string;
  readonly dateOfBirth?: string;
  readonly gender?: string;
  readonly countryOfBirth?: string;
  readonly cityOfBirth?: string;
  readonly countryOfResidence?: string;
  readonly citizenship?: string;
  readonly candidateId: number;
  readonly candidate?: Candidate;
}

export interface PersonalInformation extends Readonly<PersonalInformationData> {}
export class PersonalInformation extends BusinessObjectBase {
  static readonly entityName = 'PersonalInformation' as const;
  static readonly plural = 'personal-informations';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<PersonalInformation[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new PersonalInformation(data));
  }

  static async findOne(filter?: any): Promise<PersonalInformation | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new PersonalInformation(data) : null;
  }

  static async findById(
    id: number,
    filter?: any,
  ): Promise<PersonalInformation> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new PersonalInformation(data);
  }

  static async create(
    data: Omit<PersonalInformationData, 'id'>,
  ): Promise<PersonalInformation> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new PersonalInformation(result);
  }

  static async updateById(
    id: number,
    data: Partial<PersonalInformationData>,
  ): Promise<PersonalInformation> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new PersonalInformation(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
